import React from 'react';
import { Button, Icon, P } from '@fashionnovateam/nova-ui-toolkit';
import { startCase } from 'lodash';
import { FormControlLabel, RadioGroup } from '@mui/material';
import Radio from '@mui/material/Radio';
import ValidationAPI from '../../Lib/Api/validation';

export type Get2FACodeProps = {
  email: string;
  password: string;
  isInfluencer: boolean;
  possibleMethods: {
    email: string;
    call?: string;
    sms?: string;
  };
  setMethodUsed: (method: string) => void;
  onError: (error: string) => void;
};

function MethodEmail(props: { email: string }) {
  const { email } = props;
  return (
    <P size="sm" weight="normal" color="disabled">
      For added security, we will send a one-time to your email address{' '}
      <span className="font-semibold">{email}</span>.
    </P>
  );
}

function MethodChoice(props: {
  possibleMethods: Get2FACodeProps['possibleMethods'];
  method: string;
  setMethod: (method: string) => void;
}) {
  const { possibleMethods, method, setMethod } = props;

  return (
    <div className="flex flex-col gap-4">
      <P size="sm" weight="normal" color="disabled">
        To help protect your account, we will send you a one-time code using the
        method you select below.
      </P>
      <P size="xsm" weight="medium">
        How would you like to receive your code?
      </P>
      <RadioGroup name="method" value={method}>
        {Object.entries(possibleMethods).map(([m, v]) => (
          <FormControlLabel
            key={m}
            control={<Radio color="primary" onChange={() => setMethod(m)} />}
            value={m}
            label={
              <span>
                <b>{startCase(m)}</b>: {v}
              </span>
            }
          />
        ))}
      </RadioGroup>
    </div>
  );
}

export function Get2FACode(props: Get2FACodeProps) {
  const {
    email,
    password,
    isInfluencer,
    possibleMethods,
    setMethodUsed,
    onError,
  } = props;

  const [loading, setLoading] = React.useState(false);
  const [method, setMethod] = React.useState('email');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await ValidationAPI.get2faCodeLogin({
        email,
        password,
        codeMethod: method,
      });
      setMethodUsed(method);
    } catch (err: unknown) {
      onError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <form className="flex flex-col gap-10" onSubmit={handleSubmit}>
        <div className="flex flex-col gap-2">
          <P size="xl" weight="medium">
            Security Verification: One-time verification code
          </P>
          {!possibleMethods.call && !possibleMethods.sms ? (
            <MethodEmail email={possibleMethods.email} />
          ) : (
            <MethodChoice
              possibleMethods={possibleMethods}
              method={method}
              setMethod={setMethod}
            />
          )}
        </div>
        <Button className="w-full" type="submit" loading={loading}>
          Get code
          <Icon.ArrowRight />
        </Button>
      </form>
      <P size="xs" weight="normal" color="disabled">
        If you are having trouble, please{' '}
        <a
          href={`mailto:${
            isInfluencer
              ? 'influencerdocs@fashionnova.com'
              : 'vendormasterdata@fashionnova.com'
          }`}
          className="underline"
        >
          email support
        </a>
        .
      </P>
    </div>
  );
}
